import { centsToDollarsFormatted } from "@/lib/currencyHelpers";

export type EventFeePaymentCollection = "registration" | "on_site" | "external_system";

export interface EventFeeSummaryItem {
  name: string;
  amountCents: number;
  paymentCollection: EventFeePaymentCollection;
}

export interface PaymentSummaryLineItem {
  name: string;
  price: number;
  paymentCollection: EventFeePaymentCollection;
  subText: string;
}

export function normalizeEventFeePaymentCollection(value?: string | null): EventFeePaymentCollection {
  const normalizedValue = value
    ?.toLowerCase()
    .replace(/[^a-z]+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (normalizedValue === "on_site" || normalizedValue === "onsite") {
    return "on_site";
  }

  if (normalizedValue === "external_system" || normalizedValue === "externalsystem") {
    return "external_system";
  }

  return "registration";
}

export function toEventFeePaymentCollectionInput(
  value: EventFeePaymentCollection
): "REGISTRATION" | "ON_SITE" | "EXTERNAL_SYSTEM" {
  switch (value) {
    case "on_site":
      return "ON_SITE";
    case "external_system":
      return "EXTERNAL_SYSTEM";
    default:
      return "REGISTRATION";
  }
}

export function isExternalSystemFee(value: EventFeePaymentCollection) {
  return value === "external_system";
}

export function isOnSiteFee(value: EventFeePaymentCollection) {
  return value === "on_site";
}

export function getEventFeeDisplayName(name: string, paymentCollection: EventFeePaymentCollection) {
  switch (paymentCollection) {
    case "on_site":
      return `${name} (Onsite)`;
    case "external_system":
      return `${name} (External System)`;
    default:
      return name;
  }
}

export function getEventFeeSupportingText(amountCents: number, paymentCollection: EventFeePaymentCollection) {
  switch (paymentCollection) {
    case "on_site":
      return `Not included in your online payment, ${centsToDollarsFormatted(amountCents)} will be collected onsite.`;
    case "external_system":
      return "Shown during registration, but collected outside PGA Commerce.";
    default:
      return "";
  }
}

export function buildPaymentSummaryLineItems(items: EventFeeSummaryItem[]): PaymentSummaryLineItem[] {
  return items.map((item) => ({
    name: getEventFeeDisplayName(item.name, item.paymentCollection),
    price: item.amountCents,
    paymentCollection: item.paymentCollection,
    subText: getEventFeeSupportingText(item.amountCents, item.paymentCollection)
  }));
}

export function getPaymentSummaryTotals(items: PaymentSummaryLineItem[]) {
  const dueNowItems = items.filter((item) => item.paymentCollection === "registration");
  const dueOnSiteItems = items.filter((item) => item.paymentCollection === "on_site");
  const externalSystemItems = items.filter((item) => item.paymentCollection === "external_system");

  const dueNowTotal = dueNowItems.reduce((sum, item) => sum + item.price, 0);
  const dueOnSiteTotal = dueOnSiteItems.reduce((sum, item) => sum + item.price, 0);
  const externalSystemTotal = externalSystemItems.reduce((sum, item) => sum + item.price, 0);

  return {
    dueNowTotal,
    dueOnSiteTotal,
    externalSystemTotal,
    totalFees: dueNowTotal + dueOnSiteTotal + externalSystemTotal
  };
}
